import React, { createContext, useContext, useState, useCallback } from 'react';

const GalleryContext = createContext();

const defaultFilters = {
  search: '',
  category: '',
  disponibilite: '',
  priceRange: [0, 50000],
};

const FILTERS_KEY = 'gallery_filters';

const loadFilters = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(FILTERS_KEY));
    return saved ? { ...defaultFilters, ...saved } : defaultFilters;
  } catch {
    return defaultFilters;
  }
};

export function GalleryStoreProvider({ children }) {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [selectedTableau, setSelectedTableau] = useState(null);
  const [filters, setFiltersState] = useState(loadFilters);

  const toggleTheme = useCallback(() => setIsDarkMode((v) => !v), []);
  const selectTableau = useCallback((t) => setSelectedTableau(t), []);
  const closeTableau = useCallback(() => setSelectedTableau(null), []);

  const setFilters = useCallback((newFilters) => {
    setFiltersState((prev) => {
      const updated = { ...prev, ...newFilters };
      try { localStorage.setItem(FILTERS_KEY, JSON.stringify(updated)); } catch (e) { /* storage unavailable */ }
      return updated;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(defaultFilters);
    try { localStorage.removeItem(FILTERS_KEY); } catch (e) { /* storage unavailable */ }
  }, []);

  const getFilteredTableaux = useCallback(
    (tableaux = []) => {
      return tableaux.filter((t) => {
        if (filters.search) {
          const q = filters.search.toLowerCase();
          const inText =
            (t.titre || '').toLowerCase().includes(q) ||
            (t.description || '').toLowerCase().includes(q);
          const inTags = t.tags && t.tags.some((tag) => tag.toLowerCase().includes(q));
          if (!inText && !inTags) return false;
        }
        if (filters.disponibilite && t.disponibilite !== filters.disponibilite) {
          return false;
        }
        if (filters.category && !(t.categories || []).includes(filters.category)) {
          return false;
        }
        const price = parseFloat((t.prix || '').toString().replace('€', '').replace(',', '.')) || 0;
        if (price < filters.priceRange[0] || price > filters.priceRange[1]) {
          return false;
        }
        return true;
      });
    },
    [filters]
  );

  const value = {
    // UI state
    isDarkMode,
    showHeader,
    selectedTableau,
    toggleTheme,
    setShowHeader,
    selectTableau,
    closeTableau,
    // Filters
    filters,
    setFilters,
    resetFilters,
    getFilteredTableaux,
  };

  return <GalleryContext.Provider value={value}>{children}</GalleryContext.Provider>;
}

export default GalleryStoreProvider;
export const GalleryProvider = GalleryStoreProvider;
export const useGalleryStore = () => useContext(GalleryContext);
export const useUIStore = () => useContext(GalleryContext);
